/**
 * CheckoutPage — review delivery details, payment method and the bill, then pay.
 *
 * Matches the provided design, in the Rocket07 orange theme. It's a standalone
 * page (its own minimal header, no storefront navbar), like the cart.
 *
 * THE PAYMENT FLOW
 * ----------------
 *   1. POST /orders       — the server re-prices the cart from its own database
 *                           and opens a Razorpay order. The browser's totals are
 *                           display-only; they are never trusted for the charge.
 *   2. Razorpay Checkout  — the student pays by UPI / card / netbanking in
 *                           Razorpay's own iframe. No card data touches us.
 *   3. POST /orders/verify — the server checks the HMAC signature before it
 *                           marks the order paid. Only then is the cart cleared.
 *
 * A payment that succeeds but fails to verify (browser closed, network drop) is
 * still captured server-side by the Razorpay webhook, so money is never taken
 * without an order to show for it.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  MapPin,
  Phone,
  MessageSquare,
  ShieldCheck,
  IndianRupee,
  PartyPopper,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import { formatCurrency, describeDelivery, computeDeliveryFee } from '@rocket07/shared';
import { Container } from '@/components/layout/Container';
import { DeliveryDateCard } from '@/components/DeliveryDateCard';
import { DeliveryLocationList } from '@/components/DeliveryLocationList';
import { Input, Button, Modal, AccentCard } from '@/components/ui';
import { serverNow } from '@/lib/serverClock';
import { useCart } from '@/context/CartContext';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { useCollege } from '@/context/CollegeContext';
import { useDeliveryLocations } from '@/hooks/useStorefront';
import { ordersApi } from '@/api/orders.api';
import { loadRazorpayCheckout } from '@/lib/razorpay';
import { ROUTES } from '@/constants/routes';

export function CheckoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lines, itemCount, subtotal, clear } = useCart();
  const { customer, status } = useCustomerAuth();
  const { collegeId, college } = useCollege();

  // Public config from the college; 960 (4:00 PM IST) mirrors the model default
  // and covers the first render, before the college list has arrived.
  const cutoffMinutes = college?.orderCutoffMinutes ?? 960;

  /** The confirmed order, once payment has been verified. */
  const [paidOrder, setPaidOrder] = useState(null);
  const [payError, setPayError] = useState('');
  /** True from "Proceed to pay" until Razorpay closes — covers the whole flow. */
  const [isPaying, setIsPaying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Free campus delivery. (Change this one line to introduce a fee.)
  const deliveryFee = computeDeliveryFee(college, subtotal);
  const total = subtotal + deliveryFee;

  const { data: locations = [] } = useDeliveryLocations(collegeId);
  const hasCampusLocations = locations.length > 0;
  // Prefilled from the hostel saved on the account, so a returning student
  // does not re-pick the same block every order. Still changeable here.
  const [locationId, setLocationId] = useState(
    () => customer?.defaultLocation?._id ?? customer?.defaultLocation ?? '',
  );

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: { phone: '', location: '', notes: '' },
    // On a hard refresh of /checkout the account arrives a beat after the first
    // render, so the saved mobile has to flow in reactively rather than being
    // captured once. `keepDirtyValues` stops that from overwriting a number the
    // student has already started typing.
    values: { phone: customer?.mobile ?? '', location: '', notes: '' },
    resetOptions: { keepDirtyValues: true },
  });

  // Paying requires an account: the order has to belong to someone.
  if (status === 'unauthenticated') {
    return <Navigate to={ROUTES.login} state={{ from: ROUTES.checkout }} replace />;
  }

  // Can't check out an empty cart — but don't bounce away from the receipt.
  if (itemCount === 0 && !paidOrder) return <Navigate to={ROUTES.cart} replace />;

  const onSubmit = async (values) => {
    setPayError('');

    // The picker is not a registered field, so react-hook-form's `required`
    // cannot guard it — check it here before anything is charged.
    if (hasCampusLocations && !locationId) {
      setError('locationId', { type: 'manual', message: 'Choose where we should deliver' });
      return;
    }

    setIsPaying(true);

    try {
      // Load Checkout first: a network failure here costs nothing, whereas
      // failing after the order exists would leave an unpayable order behind.
      const Razorpay = await loadRazorpayCheckout();

      const { order, razorpayOrderId, keyId } = await ordersApi.create({
        collegeId,
        items: lines.map((line) => ({ productId: line.product.id, quantity: line.quantity })),
        // Only the id goes up; the server composes the readable address from
        // the location's real name, so the browser cannot invent one.
        delivery: hasCampusLocations
          ? {
              phone: values.phone,
              locationId,
              notes: values.notes ?? '',
            }
          : { phone: values.phone, location: values.location, notes: values.notes ?? '' },
      });

      const checkout = new Razorpay({
        key: keyId,
        // Paise, and read from the server's order — not from the browser total.
        amount: Math.round(order.total * 100),
        currency: order.currency,
        name: 'Rocket07',
        description: `${itemCount} item${itemCount > 1 ? 's' : ''} · campus delivery`,
        order_id: razorpayOrderId,
        prefill: {
          name: customer?.name ?? '',
          email: customer?.email ?? '',
          contact: values.phone,
        },
        // The server's composed address — `values.location` is undefined when
        // the campus picker is in use, which would send an empty note.
        notes: { location: order.delivery?.location ?? '' },
        // Matches --color-primary-600 so Checkout looks like the rest of the app.
        theme: { color: '#efb500' },
        /**
         * UPI first, with every flow it supports — students pay by UPI, and
         * leaving the default ordering buries it under cards.
         *
         * Naming all three flows matters because Checkout picks per device:
         *   intent  — opens GPay/PhonePe directly. MOBILE ONLY; an intent is
         *             an app deep link, so it cannot exist in a desktop browser.
         *   collect — type a UPI ID and approve in your app. Works everywhere,
         *             and is the one worth having on desktop.
         *   qr      — scan from another phone.
         * Without `collect` listed, a desktop user is left with just the QR.
         */
        config: {
          display: {
            blocks: {
              upi: {
                name: 'Pay using UPI',
                instruments: [{ method: 'upi', flows: ['intent', 'collect', 'qr'] }],
              },
            },
            sequence: ['block.upi'],
            // Cards, netbanking and wallets still follow underneath.
            preferences: { show_default_blocks: true },
          },
        },
        handler: async (response) => {
          try {
            const confirmed = await ordersApi.verify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            // Show the receipt BEFORE emptying the cart: an empty cart sends
            // this page to /cart, and setting the order first is what keeps
            // that redirect from firing over the confirmation.
            setPaidOrder(confirmed);
            clear();
            queryClient.invalidateQueries({ queryKey: ['orders', 'mine'] });
          } catch (error) {
            setPayError(
              error?.message ??
                'We could not confirm your payment. If money was debited it will be reconciled automatically — check My Orders in a minute.',
            );
          } finally {
            setIsPaying(false);
          }
        },
        modal: {
          // The student closed Checkout without paying. The pending order is
          // harmless: it holds no stock and never becomes an order until paid.
          ondismiss: () => {
            setIsPaying(false);
            setPayError('Payment cancelled. Your cart is safe — you can try again.');
          },
        },
      });

      checkout.on('payment.failed', (response) => {
        setIsPaying(false);
        setPayError(
          response?.error?.description ?? 'The payment failed. Please try another method.',
        );
      });

      checkout.open();
    } catch (error) {
      setIsPaying(false);
      setPayError(applyFieldErrors(error, setError));
    }
  };

  /**
   * The delivery day shown on the receipt.
   *
   * Anchored to when the order was actually CREATED, not to "now": the student
   * may sit on the confirmation for a while, and the batch was decided the
   * moment the order was placed. Falls back to now if the payload has no
   * timestamp, which only affects the wording, never the order.
   */
  const paidDelivery = describeDelivery(
    cutoffMinutes,
    paidOrder?.createdAt ? Date.parse(paidOrder.createdAt) : serverNow(),
  );

  return (
    <div className="min-h-dvh bg-canvas pb-28">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 flex items-center gap-3 bg-white px-4 py-3 shadow-nav">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink hover:bg-canvas"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-extrabold text-ink">Checkout</h1>
      </header>

      <Container className="max-w-2xl pt-4">
        {/* When this order actually arrives — derived from the college's cutoff. */}
        <DeliveryDateCard cutoffMinutes={cutoffMinutes} />

        {/* Delivery details */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/*
            On the tint, like the delivery-date card above it: these two blocks
            are the ones the student has to read and fill in. Bill details below
            stays on white — it is a summary, not something to act on.
          */}
          <AccentCard icon={MapPin} title="Delivery details">
            <div className="space-y-4">
              <Input
                label="Contact phone number"
                type="tel"
                leftIcon={<Phone size={16} />}
                placeholder="10-digit mobile"
                error={errors.phone?.message}
                {...register('phone', {
                  required: 'Phone number is required',
                  pattern: { value: /^[0-9]{10}$/, message: 'Enter a 10-digit mobile number' },
                })}
              />
              {/*
                Colleges that have set up campus locations get a picker; the
                rest keep the free-text box. Without that fallback, a college
                whose admin has not added any hostels yet would have a checkout
                nobody could complete.
              */}
              {hasCampusLocations ? (
                <>
                  <LocationPicker
                    locations={locations}
                    value={locationId}
                    onChange={(id) => {
                      setLocationId(id);
                      clearErrors('locationId');
                    }}
                    error={errors.locationId?.message}
                  />
                </>
              ) : (
                <Input
                  label="Delivery location on campus"
                  leftIcon={<MapPin size={16} />}
                  placeholder="e.g. Block C or Main Hostel"
                  error={errors.location?.message}
                  {...register('location', {
                    required: 'Where should we deliver?',
                    // These bounds MIRROR the server's schema. When the two
                    // drift, a value passes here and is rejected there, and the
                    // customer is told to "check the highlighted fields" with
                    // nothing highlighted — exactly what a lone "required" did.
                    minLength: {
                      value: 3,
                      message: 'Give a campus delivery location, e.g. Block C',
                    },
                    maxLength: { value: 300, message: 'That address is too long.' },
                  })}
                />
              )}
              <Input
                label="Notes for the delivery person (optional)"
                leftIcon={<MessageSquare size={16} />}
                placeholder="e.g. Call when you're at the gate"
                error={errors.notes?.message}
                {...register('notes', {
                  maxLength: { value: 500, message: 'Please keep the note under 500 characters.' },
                })}
              />
            </div>
          </AccentCard>

          {/* Payment — compact, horizontal secure-pay strip */}
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-line bg-surface px-3.5 py-2.5 shadow-card">
            <ShieldCheck size={16} className="shrink-0 text-primary-600" />
            <span className="text-xs font-semibold text-ink">Secured by Razorpay</span>
            <span className="text-xs text-muted">· UPI, cards &amp; netbanking</span>
          </div>

          {/* Bill details */}
          <AccentCard icon={IndianRupee} title="Bill details">
            <dl className="space-y-2.5 text-sm">
              <Row label={`Item total (${itemCount})`} value={formatCurrency(subtotal)} />
              <Row
                label="Delivery charge"
                value={deliveryFee === 0 ? 'FREE' : formatCurrency(deliveryFee)}
                valueClass={deliveryFee === 0 ? 'font-bold text-primary-600' : ''}
              />
              <div className="mt-1 flex items-center justify-between border-t border-dashed border-line pt-3 text-base font-extrabold text-ink">
                <dt>Total</dt>
                <dd>{formatCurrency(total)}</dd>
              </div>
            </dl>
          </AccentCard>

          {/* Anything that went wrong with the payment attempt */}
          {payError && (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2.5 rounded-xl border border-danger bg-red-50 px-4 py-3 text-sm text-danger"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{payError}</span>
            </div>
          )}

          {/* Sticky pay bar */}
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white px-4 py-3 shadow-bar">
            <div className="mx-auto max-w-2xl">
              <Button type="submit" fullWidth size="lg" isLoading={isPaying} disabled={isPaying}>
                Proceed to pay · {formatCurrency(total)}
              </Button>
            </div>
          </div>
        </form>
      </Container>

      {/* Payment confirmed — the order is placed and paid for. */}
      <Modal
        isOpen={Boolean(paidOrder)}
        onClose={() => navigate(ROUTES.orders)}
        title="Payment successful"
        footer={
          <>
            <Button variant="secondary" onClick={() => navigate(ROUTES.home)}>
              Keep shopping
            </Button>
            <Button onClick={() => navigate(ROUTES.orders)}>Track my order</Button>
          </>
        }
      >
        <div className="text-center">
          <span className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-primary-50 text-primary-600">
            <PartyPopper size={26} />
          </span>
          <p className="text-sm text-ink">
            We&apos;ve received{' '}
            <span className="font-bold">{formatCurrency(paidOrder?.total ?? 0)}</span> and sent your
            order to the campus store.
          </p>
          <p className="mt-2 text-sm text-muted">
            Order <span className="font-semibold text-ink">#{paidOrder?.reference}</span> — arriving{' '}
            {/*
              Derived, not hardcoded: an order placed after the cutoff arrives
              tomorrow, and the receipt has to say so or the student is told one
              day here and sees another in My Orders.
            */}
            {paidDelivery.dayLabel.toLowerCase()}, {paidDelivery.dateLabel} at{' '}
            {paidOrder?.delivery?.location}.
          </p>
          <Link
            to={ROUTES.orders}
            className="mt-3 inline-block text-sm font-semibold text-primary-700"
          >
            View order details
          </Link>
        </div>
      </Modal>
    </div>
  );
}

/**
 * LocationPicker — tap the box, the campus locations open in a popup.
 *
 * A POPUP RATHER THAN AN INLINE DROPDOWN
 * --------------------------------------
 * The list used to expand in place, which grew the delivery-details card and
 * pushed the bill and the pay button down the page — so choosing a hostel
 * moved the very thing the student was heading for. In a dialog the page
 * behind holds still, and the list gets the whole screen instead of being
 * squeezed into a card, which matters on a campus with a long list.
 *
 * It is also what the header's "Deliver to" already does, so picking a hostel
 * now looks the same wherever a student does it.
 *
 * Still a button + dialog rather than a native <select>: the options are
 * grouped by kind and carry a second line of description, neither of which a
 * mobile <select> can show.
 */
function LocationPicker({ locations, value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const selected = locations.find((location) => location._id === value);

  return (
    <div className="w-full">
      <span className="mb-1.5 block text-sm font-medium text-ink">Delivery location on campus</span>

      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex h-11 w-full items-center gap-2 rounded-lg border bg-white px-3 text-left text-sm ${
          error ? 'border-danger' : 'border-line'
        }`}
      >
        <MapPin size={16} className="shrink-0 text-muted" />
        <span className={selected ? 'flex-1 truncate text-ink' : 'flex-1 truncate text-muted'}>
          {selected ? selected.name : 'Choose your hostel or block'}
        </span>
        <ChevronDown size={16} className="shrink-0 text-muted" />
      </button>

      {error && <p className="mt-1.5 text-xs font-medium text-danger">{error}</p>}

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Where should we deliver?">
        <DeliveryLocationList
          locations={locations}
          selectedId={value}
          onSelect={(id) => {
            onChange(id);
            setOpen(false);
          }}
        />
      </Modal>
    </div>
  );
}

/**
 * Puts the API's per-field validation errors onto the fields they belong to.
 *
 * The server answers a 422 with `errors: [{ field, message }]`, but the form
 * only ever showed the summary line ("Please check the highlighted fields"),
 * so nothing was highlighted and the customer had no idea what to change.
 *
 * Server paths are nested (`delivery.location`); the form is flat (`location`).
 * Anything that does not map to a visible field — a bad `collegeId`, a stale
 * `items.0.productId` — is not something the customer can fix by typing, so it
 * is returned as banner text instead of silently vanishing.
 *
 * @returns {string} the message to show in the banner
 */
function applyFieldErrors(error, setError) {
  const fallback = error?.message ?? 'Unable to start the payment. Please try again.';
  const issues = error?.errors ?? [];
  if (issues.length === 0) return fallback;

  const FORM_FIELDS = new Set(['phone', 'location', 'notes', 'locationId']);
  const unmapped = [];
  let mappedAny = false;

  for (const issue of issues) {
    const name = String(issue.field ?? '').replace(/^delivery\./, '');
    if (FORM_FIELDS.has(name)) {
      setError(name, { type: 'server', message: issue.message });
      mappedAny = true;
    } else {
      unmapped.push(issue.message);
    }
  }

  if (unmapped.length > 0) {
    return `${unmapped.join(' ')} Refresh your cart and try again.`;
  }
  return mappedAny ? 'Please fix the highlighted fields above.' : fallback;
}

function Row({ label, value, valueClass = '' }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className={valueClass || 'font-medium text-ink'}>{value}</dd>
    </div>
  );
}

export default CheckoutPage;
